import { IDesignerState } from '@/../typings';
import { filestore, FsFile, FsImage } from '@/filestore';
import { JUTE_VERSION, DEFAULT_LAUNCH_SCREEN, ENVIRONMENT } from '@/settings';
import { store } from '@/store';
import { finaliseContentDoc } from '@/utils/pure';
import { IActionCategory, IActionDict, IComponentType, IContent360Scene, IContentDoc, ISpatialComponentUnion } from '@r3f-component-data-structure';
import { ILaunchScreen } from '@r3f-component-data-structure/types/launchScreen';
import { zip, AsyncZippable } from 'fflate';
import { convertStringArrayToITuple4 } from '../../menus/InspectorMenu/ProjectShelves/LaunchScreen/utils';

interface IFileForBundler {
    path: string
    bin?: Uint8Array
}

export async function bundleProject() {
    const tStart = performance.now();
    const doc: IContentDoc = finaliseContentDoc((store.getState() as IDesignerState).contentReducer.contentDoc)
    // Download all media files, referenced in the doc, that will be added to the zip
    const mediaFilesPromises = _getMediaFiles(doc)

    const launchScreen = doc.launchScreen || DEFAULT_LAUNCH_SCREEN;
    let lsRootUrl = `https://media.zap.works/launch-screen/${launchScreen.id}`;
    if (ENVIRONMENT !== 'production') {
        lsRootUrl = launchScreen.id !== 'lite-branded' ? `https://media.zap.works/launch-screen-stg/${launchScreen.id}` : `https://media.zap.works/launch-screen-stg/default`;
    }

    const lsManifest = await (await fetch(`${lsRootUrl}/.zw-manifest.json`)).json()
    const launchScreenPromises: Promise<IFileForBundler[]>[] = []
    for (const path of lsManifest.files) {
        // eslint-disable-next-line no-async-promise-executor
        launchScreenPromises.push(new Promise(async resolve => {
            const res = await fetch(`${lsRootUrl}/${path}`)
            if (path === 'index.html') {
                const indexFiles = await _renderLaunchScreenIndex(await res.text(), launchScreen)
                resolve(indexFiles)
            } else {
                resolve([{ path, bin: new Uint8Array(await res.arrayBuffer()) }])
            }
        }))
    }

    const filesArray = await Promise.all([mediaFilesPromises, ...launchScreenPromises])
    const filesToZip: AsyncZippable = {}
    for (const files of filesArray) {
        for (const fl of files) {
            // console.log('Media:', fl)
            if (typeof fl.bin !== 'undefined') filesToZip[fl.path] = fl.bin
        }
    }
    filesToZip['content.json'] = (new TextEncoder()).encode(JSON.stringify(doc))

    console.log('Bundling took', performance.now() - tStart, 'ms')
    return new Promise<Blob>((resolve, reject) => {
        zip(filesToZip, { level: 8 }, (err, zipFile) => {
            if (err) return reject(err)
            const file = new File([zipFile as unknown as ArrayBuffer], 'project.zip', { type: 'application/zip' })
            // console.log(`The project size is: ${file.size / (1024 * 1024)} MB`);
            resolve(file)
        })
    })
}

async function _getMediaFiles(doc: IContentDoc) {
    const files: FsFile[] = []
    for (const comp of Object.values(doc.componentsById)) {
        if (comp.type === IComponentType.Image || comp.type === IComponentType.Model3d || comp.type === IComponentType.Video) {
            files.push(filestore.load(comp.filestoreId))
        } else if (comp.type === IComponentType.Scene) {
            const content360Scene = comp as IContent360Scene
            if (content360Scene.content360) {
                files.push(filestore.load(content360Scene.content360.filestoreId))
            }
        }
        if (comp.type === IComponentType.Root && typeof comp.bkgrSound?.filestoreId !== 'undefined') {
            files.push(filestore.load(comp.bkgrSound.filestoreId))
        }
        files.push(..._getActionsMediaFiles((comp as ISpatialComponentUnion).actions)) // Scene, Button, Image, etc.
    }
    if (typeof doc.tracking?.image !== 'undefined') {
        const trackingImageDict = doc.tracking!.image;
        for (const trackingData of Object.values(trackingImageDict)) {
            files.push(filestore.load(trackingData.filestoreId))
        }
    }

    const promises: Promise<IFileForBundler[]>[] = Object.values(files).map(async fsFile => {
        return await fsFile.getFilesForBundler()
    })
   
    const filesForBundler = await Promise.all(promises)
    // return a flat array of files
    return filesForBundler.reduce((acc, val) => acc.concat(val), [])
}

function _getActionsMediaFiles(actionsByTrigger?: IActionDict) {
    const files: FsFile[] = []
    if (typeof actionsByTrigger === 'undefined') return files
    for (const [_trigger, actions] of Object.entries(actionsByTrigger)) {
        for (const action of actions) {
            if (action.type === IActionCategory.playSound) {
                files.push(filestore.load(action.filestoreId))
            }
        }
    }
    return files
}

async function _renderLaunchScreenIndex(indexHtml: string, launchScreen: ILaunchScreen) {
    indexHtml = indexHtml.replace('</body>', `<script src="https://libs.zappar.com/jute/${JUTE_VERSION}/index.js"></script></body>`)
    // indexHtml = indexHtml.replace(new RegExp('{{ JUTE_VERSION }}', 'g'), JUTE_VERSION)
    indexHtml = indexHtml.replace(new RegExp('{{ TITLE }}', 'g'), launchScreen.fields?.title?.value || 'An Augmented Reality Experience')

    const iconFilestoreId = launchScreen?.fields?.iconFilestoreId?.value;
    let iconUrl = './icon.svg';

    let fsImage: FsImage | undefined;

    if (iconFilestoreId) {
        fsImage = filestore.load<FsImage>(iconFilestoreId);
        iconUrl = `./${fsImage.getBundlerPath()}`;
    }    

    indexHtml = indexHtml.replace(new RegExp('{{ ICON_URL }}', 'g'), iconUrl)
    
    indexHtml = indexHtml.replace(new RegExp('{{ BUTTON_TEXT }}', 'g'), launchScreen.fields?.buttonText?.value || 'Launch')
    indexHtml = indexHtml.replace(new RegExp('{{ ICON_SIZE }}', 'g'), launchScreen.fields?.iconSize?.value || 'medium')
    
    let bgColor = launchScreen.fields?.backgroundColorRGBA?.value || '[255,255,255,1]'
    let elementColor = launchScreen.fields?.elementColorRGBA?.value || '[7,49,88,1]'
    if (bgColor.startsWith('[') || elementColor.startsWith('[')) {
        bgColor = `${convertStringArrayToITuple4(bgColor)}`
        elementColor =`${convertStringArrayToITuple4(elementColor)}`
    }
    indexHtml = indexHtml.replace(new RegExp('{{ BACKGROUND_COLOR }}', 'g'), bgColor || '')
    indexHtml = indexHtml.replace(new RegExp('{{ ELEMENT_COLOR }}', 'g'), elementColor || '')
    
    indexHtml = indexHtml.replace(new RegExp('{{ HIDDEN_CUSTOM_ICON }}', 'g'), iconFilestoreId ? '' : 'hidden')
    indexHtml = indexHtml.replace(new RegExp('{{ HIDDEN_DEFAULT_ICON }}', 'g'), iconFilestoreId ? 'hidden' : '')
    const encoder = new TextEncoder()
    const res = [{ path: 'index.html', bin: (encoder.encode(indexHtml) as unknown as Uint8Array<ArrayBuffer | ArrayBufferLike>) }]
    if (fsImage) {
        res.push(...(await fsImage.getFilesForBundler()))
    }
    return res
}
